import React, { useRef, useState } from "react";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Add, CancelOutlined, Check, DeleteOutline } from "@mui/icons-material";
import {
  DataGridPro,
  GridEditInputCell,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import { ContactRow, ContactUpload } from "./ContactUpload";
import { validateEmailAddress } from "src/utils/helpers";
import ImportFooter from "../ImportFooter";
import PrimaryButton from "src/componentsV2/buttons/PrimaryButton";

function isValidContact(contact: ContactRow): boolean {
  const contactEmail = contact.email?.trim();
  if (!validateEmailAddress(contactEmail)) {
    return false;
  }

  const coHostEmail = contact.meetingCoHost?.trim();
  if (contactEmail === coHostEmail) {
    return false;
  }

  return coHostEmail ? validateEmailAddress(coHostEmail) : true;
}

function validateIsUnsubscribed(s: string | undefined | null): boolean {
  if (!s) {
    return true;
  }
  const allowed = ["true", "t", "1", "false", "f", "0"];
  return allowed.includes(s.trim().toLowerCase());
}

type Row = ContactRow & {
  error: boolean;
  meetingCoHost: string;
  isUnsubscribed: string;
};

const createEmptyRow = (id: number): Row => ({
  row: id,
  firstName: "",
  lastName: "",
  email: "",
  account: "",
  phone: "",
  zip: "",
  state: "",
  meetingCoHost: "",
  logicField: "",
  leadScore: "",
  routingField: "",
  customField1: "",
  customField2: "",
  customField3: "",
  customField4: "",
  customField5: "",
  customField6: "",
  customField7: "",
  customField8: "",
  customField9: "",
  customField10: "",
  isUnsubscribed: "",
  error: true,
});

export function Validation({
  contacts,
  onPrev = () => null,
  onNext = () => null,
}: {
  contacts: ContactUpload;
  onPrev: () => void;
  onNext: (contacts: ContactUpload) => void;
}) {
  const apiRef = useGridApiRef();

  const [data, setData] = useState<Row[]>(
    contacts.map((contact, index) => ({
      ...contact,
      firstName: contact.firstName?.trim(),
      lastName: contact.lastName?.trim(),
      email: contact.email?.trim(),
      row: index,
      error:
        !isValidContact(contact) ||
        !validateIsUnsubscribed(contact.isUnsubscribed),
      meetingCoHost: contact.meetingCoHost?.trim(),
      isUnsubscribed: contact.isUnsubscribed?.trim(),
    })),
  );

  const [errors, setErrors] = useState(
    data
      .filter((row) => row.error)
      .reduce(
        (prev, current) => {
          prev[current.row] = current;
          return prev;
        },
        {} as { [index: number]: ContactUpload[0] },
      ),
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const numErrors = Object.entries(errors).length;

  const nextID = useRef(data.length);

  const addRow = () => {
    const id = nextID.current++;

    setData((prev) => {
      const newRow = createEmptyRow(id);
      const next = [...prev, newRow];

      setErrors((prev) => ({
        ...prev,
        [newRow.row]: newRow as ContactUpload[0],
      }));

      // nav to last page
      setPage(Math.floor((next.length - 1) / pageSize));

      return next;
    });
  };

  const deleteRow = (id: GridRowId) => {
    setData((prev) => prev.filter((row) => row.row !== id));
  };

  return (
    <>
      <Box sx={{ width: "100%", minHeight: "400px" }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            {numErrors > 0 ? (
              <Stack spacing={1}>
                <Typography>
                  It looks like there are a few issues with some of the data.
                  Please fix the {numErrors}{" "}
                  {numErrors === 1 ? "error" : "errors"} shown below.
                </Typography>
              </Stack>
            ) : (
              <Typography>Your upload looks ready to go!</Typography>
            )}
            <PrimaryButton onClick={addRow}>
              <Add />
              Add Row
            </PrimaryButton>
          </Stack>
          <Paper elevation={0}>
            <DataGridPro
              apiRef={apiRef}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    row: true,
                    status: true,
                    firstName: true,
                    lastName: true,
                    email: true,
                    account: data.some((row) => row.account),
                    phone: data.some((row) => row.phone),
                    zip: data.some((row) => row.zip),
                    state: data.some((row) => row.state),
                    meetingCoHost: data.some((row: Row) => row.meetingCoHost),
                    logicField: data.some((row) => row.logicField),
                    leadScore: data.some((row) => row.leadScore),
                    routingField: data.some((row) => row.routingField),
                    customField1: data.some((row) => row.customField1),
                    customField2: data.some((row) => row.customField2),
                    customField3: data.some((row) => row.customField3),
                    customField4: data.some((row) => row.customField4),
                    customField5: data.some((row) => row.customField5),
                    customField6: data.some((row) => row.customField6),
                    customField7: data.some((row) => row.customField7),
                    customField8: data.some((row) => row.customField8),
                    customField9: data.some((row) => row.customField9),
                    customField10: data.some((row) => row.customField10),
                    isUnsubscribed: data.some((row) => row.isUnsubscribed),
                  },
                },
              }}
              sx={{
                height: 54 * (pageSize + 2),
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  backgroundColor: "background.paper",
                },
                "& .MuiDataGrid-pinnedColumns": {
                  backgroundColor: "background.paper",
                },
                "& .MuiDataGrid-pinnedColumns .MuiDataGrid-cell": {
                  backgroundColor: "background.paper",
                },
              }}
              rowHeight={54}
              pagination
              rowsPerPageOptions={[10, 25, 100]}
              editMode="row"
              experimentalFeatures={{
                newEditingApi: true,
                // preventCommitWhileValidating: false,
              }}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              pinnedColumns={{
                right: ["remove"],
              }}
              columns={[
                {
                  field: "row",
                  valueGetter: (params) =>
                    page * pageSize +
                    apiRef.current.getRowIndexRelativeToVisibleRows(params.id) +
                    1,
                  headerName: "Row",
                  editable: false,
                  minWidth: 20,
                  hideable: false,
                },
                {
                  field: "error",
                  headerName: "Valid",
                  editable: false,
                  minWidth: 40,
                  hideable: true,
                  renderCell: (params) =>
                    params.value ? (
                      <CancelOutlined color="error" />
                    ) : (
                      <Check color="success" />
                    ),
                },
                {
                  field: "firstName",
                  headerName: "First Name",
                  editable: true,
                  minWidth: 150,
                  hideable: false,
                },
                {
                  field: "lastName",
                  headerName: "Last Name",
                  editable: true,
                  minWidth: 150,
                  hideable: false,
                },
                {
                  field: "email",
                  headerName: "Email",
                  editable: true,
                  minWidth: 250,
                  hideable: false,
                  renderCell: (props) => {
                    const error = !validateEmailAddress(props.value);
                    return (
                      <Typography color={error ? "error" : undefined}>
                        {props.value}
                      </Typography>
                    );
                  },
                  renderEditCell: (props) => {
                    return <GridEditInputCell {...props} />;
                  },
                },
                {
                  field: "account",
                  headerName: "Account",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "phone",
                  headerName: "Phone",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "zip",
                  headerName: "Zip",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "state",
                  headerName: "State",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "meetingCoHost",
                  headerName: "Meeting Co-Host",
                  editable: true,
                  minWidth: 250,
                  renderCell: (props) => {
                    const error =
                      !validateEmailAddress(props.value) ||
                      props.row.email?.trim() === props.value?.trim();
                    return (
                      <Typography color={error ? "error" : undefined}>
                        {props.value}
                      </Typography>
                    );
                  },
                  renderEditCell: (props) => {
                    return <GridEditInputCell {...props} />;
                  },
                },
                {
                  field: "logicField",
                  headerName: "Logic Field 1",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "leadScore",
                  headerName: "Logic Field 2",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "routingField",
                  headerName: "Routing Field",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField1",
                  headerName: "Custom Field 1",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField2",
                  headerName: "Custom Field 2",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField3",
                  headerName: "Custom Field 3",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField4",
                  headerName: "Custom Field 4",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField5",
                  headerName: "Custom Field 5",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField6",
                  headerName: "Custom Field 6",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField7",
                  headerName: "Custom Field 7",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField8",
                  headerName: "Custom Field 8",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField9",
                  headerName: "Custom Field 9",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "customField10",
                  headerName: "Custom Field 10",
                  editable: true,
                  minWidth: 150,
                },
                {
                  field: "unsubscribed",
                  headerName: "Is Unsubscribed",
                  editable: true,
                  minWidth: 150,
                  hideable: false,
                  renderCell: (props) => {
                    const error = !validateIsUnsubscribed(props.value);
                    return (
                      <Typography color={error ? "error" : undefined}>
                        {props.value?.toLowerCase()}
                      </Typography>
                    );
                  },
                  renderEditCell: (props) => {
                    return <GridEditInputCell {...props} />;
                  },
                },
                {
                  field: "remove",
                  headerName: "",
                  editable: false,
                  width: 46,
                  minWidth: 46,
                  maxWidth: 46,
                  hideable: false,
                  disableColumnMenu: true,
                  sortable: false,
                  renderCell: (params) => {
                    return (
                      <IconButton
                        onClick={() => {
                          deleteRow(params.id);

                          setErrors((prev) => {
                            const copy = { ...prev };
                            delete copy[params.row.row];
                            return copy;
                          });
                        }}
                        size="small"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    );
                  },
                },
              ]}
              rows={data}
              getRowId={(row) => row.row}
              processRowUpdate={(updatedRow) => {
                const rowWithValidation = {
                  ...updatedRow,
                  error:
                    !isValidContact(updatedRow) ||
                    !validateIsUnsubscribed(updatedRow.isUnsubscribed),
                };

                setData((prev) =>
                  prev.map((r) =>
                    r.row === rowWithValidation.row ? rowWithValidation : r,
                  ),
                );

                if (rowWithValidation.error) {
                  setErrors((prev) => ({
                    ...prev,
                    [rowWithValidation.row]: rowWithValidation,
                  }));
                } else {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next[rowWithValidation.row];
                    return next;
                  });
                }

                return rowWithValidation;
              }}
            />
          </Paper>
        </Stack>
      </Box>
      <ImportFooter
        onLeftButtonClick={onPrev}
        onRightButtonClick={() => {
          onNext(
            Object.values(
              apiRef.current.state.rows.idRowsLookup,
            ) as ContactUpload,
          );
        }}
        rightButtonDisabled={numErrors !== 0 || data.length < 1}
      />
    </>
  );
}

export default Validation;
